/* istanbul ignore file */
import React, { createContext, useState, useCallback, useContext, useEffect } from "react";
import { appStorage, parseQueryString } from "../utils/utils";
import { getConfig, getAppConfiguration } from "../utils/network";
import useLoader from "./use-loader";
import getSource from "../utils/get-source";
import defaultConfig from "../default-app-config";

const supportedLanguages = {
    en: "English",
    hi: "हिंदी",
    // gu: 'ગુજરાતી',
    // be: 'বাংলা',
    mr: "मराठी",
    // kn: 'ಕನ್ನಡ',
    // ml: 'മലയാളം',
    // pa: 'ਪੰਜਾਬੀ',
    // te: 'తెలుగు',
    // ta: 'தமிழ்',
    // or: 'ଓଡିଆ',
    // fr: 'français',
    // sp: 'española',
};

const appSupportedLang = {
    eng: "English",
    hin: "Hindi",
    mar: "Marathi",
    ben: "Bengali",
    guj: "Gujarati",
    pan: "Punjabi",
    kan: "Kannada",
    tam: "Tamil",
    tel: "Telugu",
    mal: "Malayalam",
    ori: "Odia",
    spa: "Spanish",
    fra: "French",
};

export const TranslationContext = createContext({
    language: "en",
    t: (string) => {},
    supportedLanguages,
    setLanguage: () => {},
});

export const getDefaultLanguage = () => {
    const storedPref = appStorage.getItem("lang-pref");
    if (storedPref) return storedPref;

    let langFromQueryParams = parseQueryString().lang;
    if (langFromQueryParams && (supportedLanguages[langFromQueryParams] || appSupportedLang[langFromQueryParams]))
        return langFromQueryParams;

    return window.navigator.language.split("-") === "hi" ? "hi" : "en";
};

export const TranslationProvider = ({ children }) => {
    const [language, setLang] = useState(getDefaultLanguage());
    const [configObject, setConfigObject] = useState(null);
    const [sourceObject, setSourceObject] = useState(null);
    const [configAppObject, setConfigAppObject] = useState(null);
    const { showLoading, hideLoading } = useLoader();
    appStorage.setItem("language_name", (configObject && configObject.language) || "English" || getDefaultLanguage());
    const t = useCallback(
        (key) => configObject.localisation_strings[key] || defaultConfig.localisation_strings[key] || ""
    );
    const canOptForSurakshaKavach = () =>
        !!configAppObject.FEATURE_SURAKSHA_KAVACH_ENABLED && sourceObject.source_id === "MyJioApp";
    const shouldMandateProfile = () =>
        !!configAppObject.FEATURE_MANDATE_PROFILE && sourceObject.source_id === "MyJioApp";
    const canShowProfile = () => !!configAppObject?.FEATURE_SHOW_PROFILE && sourceObject.source_id === "MyJioApp";
    const hasChatBotQuestionnaire = () => sourceObject?.display_type === "chat";
    useEffect(() => {
        (async () => {
            showLoading();
            const data = await getConfig(language);
            setConfigObject(data);
            const configAppObject = await getAppConfiguration();
            setConfigAppObject(configAppObject);
            const sourceData = await getSource();
            setSourceObject(sourceData);
            setLang(language);
            appStorage.setItem("lang-pref", language);
            hideLoading();
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        !configObject ? showLoading() : hideLoading();
    }, [hideLoading, configObject, showLoading]);

    const setLanguage = useCallback(
        async (language) => {
            showLoading();
            const data = await getConfig(language);
            setConfigObject(data);
            setLang(language);
            hideLoading();
            appStorage.setItem("lang-pref", language);
        },
        [hideLoading, showLoading]
    );

    return (
        <TranslationContext.Provider
            value={{
                language,
                t,
                setLanguage,
                supportedLanguages,
                canOptForSurakshaKavach,
                shouldMandateProfile,
                canShowProfile,
                hasChatBotQuestionnaire,
            }}
        >
            {configObject && sourceObject ? children : <div />}
        </TranslationContext.Provider>
    );
};

export default () => useContext(TranslationContext);
